import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';

@Component({
  selector: 'laas-time-slider',
  templateUrl: './time-slider.component.html',
  styleUrls: ['./time-slider.component.scss']
})
export class TimeSliderComponent implements OnChanges {

  @Input() value: Date | null = null;
  @Output() valueChange = new EventEmitter<Date>();
  @Input() showSeconds = false;
  isTyping = false;

  hour = 0;
  minute = 0;
  second = 0;
  hourText = '00';
  minuteText = '00';
  secondText = '00';

  ngOnChanges(changes: SimpleChanges) {
    if (!changes['value']) return;

    if (this.isTyping) {
      return;
    }

    const v = changes['value'].currentValue as Date | null;
    if (!v) {
      const now = new Date();
      this.hour = now.getHours();
      this.minute = now.getMinutes();
      this.second = this.showSeconds ? now.getSeconds() : 0;
    } else {
      this.hour = v.getHours();
      this.minute = v.getMinutes();
      this.second = this.showSeconds ? v.getSeconds() : 0;
    }
    this.syncText();
  }

  onInputText(event: Event, type: 'hour' | 'minute' | 'second') {
    this.isTyping = true;
    const input = event.target as HTMLInputElement | null;
    if (!input) return;

    let raw = input.value ?? '';
    let digits = raw.replace(/\D+/g, '');
    if (digits.length > 2) {
      digits = digits.slice(0, 2);
    }
    if (digits === '') {
      if (type === 'hour') this.hourText = '';
      if (type === 'minute') this.minuteText = '';
      if (type === 'second') this.secondText = '';
      return;
    }
    let num = Number(digits);
    let max: number;

    switch (type) {
      case 'hour':
        max = 23;
        break;
      case 'minute':
      case 'second':
        max = 59;
        break;
    }
    if (type === 'hour') {
      this.hour = Math.min(num, 23);
      this.hourText = digits;
    }
    if (type === 'minute') {
      this.minute = Math.min(num, 59);
      this.minuteText = digits;
    }
    if (type === 'second') {
      this.second = Math.min(num, 59);
      this.secondText = digits;
    }
    if (num > max) num = max;
    if (type === 'hour') this.hour = num;
    if (type === 'minute') this.minute = num;
    if (type === 'second') this.second = num;

    input.value =
      type === 'hour' ? this.hourText :
        type === 'minute' ? this.minuteText :
          this.secondText;
    this.emitUpdated();
  }

  onBlur(type: 'hour' | 'minute' | 'second') {
    this.isTyping = false;
    if (type === 'hour') {
      this.hour = Math.min(this.hour || 0, 23);
    }
    if (type === 'minute') {
      this.minute = Math.min(this.minute || 0, 59);
    }
    if (type === 'second') {
      this.second = Math.min(this.second || 0, 59);
    }
    this.syncText();
    this.emitUpdated();
  }

  selectAll(event: Event) {
    const input = event.target as HTMLInputElement | null;
    input?.select();
  }

  private pad(n: number): string {
    return n.toString().padStart(2, '0');
  }

  private syncText() {
    this.hourText = this.pad(this.hour);
    this.minuteText = this.pad(this.minute);
    this.secondText = this.pad(this.second);
  }

  private emitUpdated() {
    if (!this.value) this.value = new Date();
    const updated = new Date(this.value);
    updated.setHours(this.hour);
    updated.setMinutes(this.minute);
    updated.setSeconds(this.second);
    this.valueChange.emit(updated);
  }

  incrementHour() {
    this.hour = (this.hour + 1) % 24;
    this.syncText();
    this.emitUpdated();
  }

  decrementHour() {
    this.hour = (this.hour - 1 + 24) % 24;
    this.syncText();
    this.emitUpdated();
  }

  incrementMinute() {
    this.minute = (this.minute + 1) % 60;
    this.syncText();
    this.emitUpdated();
  }

  decrementMinute() {
    this.minute = (this.minute - 1 + 60) % 60;
    this.syncText();
    this.emitUpdated();
  }

  incrementSecond() {
    this.second = (this.second + 1) % 60;
    this.syncText();
    this.emitUpdated();
  }

  decrementSecond() {
    this.second = (this.second - 1 + 60) % 60;
    this.syncText();
    this.emitUpdated();
  }

}
